import { action, observable, makeObservable, runInAction, computed } from 'mobx'
import { validateCaptcha } from 'react-simple-captcha'

import AsyncStore from 'stores/AsyncStore'
import InputStore from 'stores/InputStore'
import platformsMock from 'mocks/CustomerSelfManagement/platforms.json'
import CustomerSelfManagementService from 'services/CustomerSelfManagementService'
import { CUSTOMER_SELF_MANAGEMENT } from 'routing/routes'
import { CAPTCHA, LICENSE_PLATE, REQUIRED, REQUIRED_DNI_CUIL } from 'util/validationMapping'
import { clearLightSession, extractPlateFromAsset, getLightSession, normalizePlate, normalizePolicyNumber, saveLightSession, withIdentityNumberQuery } from 'util/customerSelfManagementSession'

import InspectionStore from './InspectionStore'
import PaymentRejectionStore from './PaymentRejectionStore'
import PolicyStore from './PolicyStore'
import AccidentStore from './AccidentStore'

export { COLLECTIONS_STATUS } from './PaymentRejectionStore'
export { INSPECTION_TAG, INSPECTION_STATUS } from './InspectionStore'

export const CUSTOMER_SELF_MANAGEMENT_AMENDMENT_TYPES = {
    PAYMENT_CHANGES: {
        id: 'paymentChanges',
        value: 'Cambiar forma de pago'
    },
    POLICY_CHANGES: {
        id: 'policyChanges',
        value: 'Modificar datos de póliza'
    }
}

export const CUSTOMER_SELF_MANAGEMENT_STEPS = {
    DOCUMENTS: 'Mi seguro',
    CHANGE_PAYMENT_METHOD: 'Cambiar forma de pago',
    MODIFY_POLICY_DATA: 'Modificar datos de póliza',
    PAYMENT_REJECTION: 'Rechazos de pagos',
    CONTACT: 'Consultas y pedidos',
    ACCIDENT: 'Siniestros',
    INSPECTIONS: 'Inspección',
    CLIENT: 'Cliente'
}

export const CUSTOMER_SELF_MANAGEMENT_DOCUMENTS = {
    POLICY: 'poliza',
    MERCOSUR: 'mercosur',
    PROVISIONAL: 'provisorio',
    CIRCULATION: 'circulacion',
    AMENDMENT: 'endoso',
}

export const CUSTOMER_SELF_MANAGEMENT_SOCIAL_NETWORKS = {
    FACEBOOK: 'facebook',
    YOUTUBE: 'youtube',
    LINKEDIN: 'linkedin',
    INSTAGRAM: 'instagram'
}

const STATIC_PLATFORMS = platformsMock.platforms

class CustomerSelfManagementStore extends AsyncStore {
    constructor() {
        super()
        this.customerSelfManagementService = new CustomerSelfManagementService()

        this.platforms = STATIC_PLATFORMS
        this.loadingPlatforms = true
        this.platformResolved = false

        this.platform = null
        this.metatitle = null
        this.logo = null
        this.contactInfo = null
        this.links = []
        this.footer = {}
        this.colors = {
            primary: '#4dd642',
            secondary: '#2d3339'
        }

        this.step = null
        this.sessionIdentityNumber = null
        this.sessionPlate = null
        this.sessionPolicyNumber = null

        // soft login
        this.identityNumber = new InputStore(REQUIRED_DNI_CUIL)
        this.captcha = new InputStore(CAPTCHA)
        this.plate = new InputStore(LICENSE_PLATE)
        this.policyNumberInput = new InputStore(REQUIRED)

        this.policyStore = new PolicyStore(this)
        this.paymentRejectionStore = new PaymentRejectionStore(this)
        this.inspectionStore = new InspectionStore(this)
        this.accidentStore = new AccidentStore(this)

        this.makeObservables()
        this.hydrateLightSession()
    }

    hydrateLightSession() {
        const session = getLightSession()

        if (!session?.identityNumber) {
            return
        }

        this.setIdentityNumber(session.identityNumber)
        this.sessionIdentityNumber = session.identityNumber
        this.sessionPlate = session.plate || null
        this.sessionPolicyNumber = session.policyNumber || null

        if (session.plate) {
            this.setPlate(normalizePlate(session.plate))
        }

        if (session.policyNumber) {
            this.setPolicyNumberInput(session.policyNumber)
        }
    }

    persistLightSession() {
        const identityNumber = this.identityNumber?.value

        if (!identityNumber) {
            return
        }

        const plate = normalizePlate(this.plate?.value) || null
        const policyNumber = this.policyNumberInput?.value || null
        const sessionPolicyNumber = plate ? null : policyNumber

        // Un solo identificador de vehículo/póliza en soft session (no mezclar).
        saveLightSession({
            identityNumber,
            plate,
            policyNumber: sessionPolicyNumber,
        })
        this.sessionIdentityNumber = identityNumber
        this.sessionPlate = plate
        this.sessionPolicyNumber = sessionPolicyNumber
    }

    /** Solo DNI en cookie (p. ej. tras validar identidad, antes de confirmar póliza/patente). */
    persistIdentityLightSession() {
        const identityNumber = this.identityNumber?.value

        if (!identityNumber) {
            return
        }

        saveLightSession({
            identityNumber,
            plate: null,
            policyNumber: null,
        })
        this.sessionIdentityNumber = identityNumber
        this.sessionPlate = null
        this.sessionPolicyNumber = null
    }

    /** Quita patente/póliza de cookie y soft session; mantiene inputs para mostrar el error. */
    clearVehicleLightSession() {
        this.sessionPlate = null
        this.sessionPolicyNumber = null
        this.policyStore.clearPolicyData()
        this.paymentRejectionStore.hydrateFromPolicy({})

        const identityNumber = this.identityNumber?.value

        if (identityNumber) {
            saveLightSession({
                identityNumber,
                plate: null,
                policyNumber: null,
            })
            this.sessionIdentityNumber = identityNumber
        }
    }

    clearIdentityLightSession() {
        clearLightSession()
        this.sessionIdentityNumber = null
        this.sessionPlate = null
        this.sessionPolicyNumber = null
    }

    closeSession() {
        clearLightSession()
        this.sessionIdentityNumber = null
        this.sessionPlate = null
        this.sessionPolicyNumber = null
        this.resetStore()
    }

    stepFromPath(pathname = '') {
        if (pathname.includes('/documentos')) {
            return CUSTOMER_SELF_MANAGEMENT_STEPS.DOCUMENTS
        }

        if (pathname.includes('/inspecciones')) {
            return CUSTOMER_SELF_MANAGEMENT_STEPS.INSPECTIONS
        }

        if (pathname.includes('/siniestros')) {
            return CUSTOMER_SELF_MANAGEMENT_STEPS.ACCIDENT
        }

        if (pathname.includes('/rechazos')) {
            return CUSTOMER_SELF_MANAGEMENT_STEPS.PAYMENT_REJECTION
        }

        if (pathname.includes('/pagos')) {
            return CUSTOMER_SELF_MANAGEMENT_STEPS.CHANGE_PAYMENT_METHOD
        }

        if (pathname.includes('/datos')) {
            return CUSTOMER_SELF_MANAGEMENT_STEPS.MODIFY_POLICY_DATA
        }

        return this.step
    }

    changeVehicleSession(history) {
        if (!this.step && history?.location?.pathname) {
            this.setStep(this.stepFromPath(history.location.pathname))
        }

        this.setPlate(null)
        this.setPolicyNumberInput(null)
        this.policyStore.clearPolicyData()
        this.paymentRejectionStore.hydrateFromPolicy({})
        this.clearErrors()
        this.persistLightSession()
        this.goToCredentials(history)
    }

    syncSessionVehicleFromPolicy() {
        // Solo completar patente cuando la sesión es por dominio (no al buscar por nro de póliza).
        if (this.policyNumberInput?.value && !normalizePlate(this.plate?.value)) {
            return
        }

        const extracted = extractPlateFromAsset(this.asset)

        if (!extracted) {
            return
        }

        const current = normalizePlate(this.plate?.value)

        // No pisar una patente explícita (deep link) con otra distinta del asset cargado.
        if (current && current !== extracted) {
            return
        }

        this.setPlate(extracted)
    }

    goToCredentials(history, step) {
        if (step) {
            this.setStep(step)
        }

        if (!history || !this.platform) {
            return
        }

        history.replace(withIdentityNumberQuery(
            `${CUSTOMER_SELF_MANAGEMENT}/${this.platform}/clientes`,
            this.identityNumber?.value,
            this.plate?.value,
            this.policyNumberInput?.value
        ))
    }

    async ensurePolicyLoaded() {
        if (this.policyId) {
            return true
        }

        const plate = normalizePlate(this.plate?.value)

        if (plate) {
            if (plate !== this.plate?.value) {
                this.setPlate(plate)
            }

            return this.checkUserPolicies('plate', { skipValidation: true })
        }

        if (this.policyNumberInput?.value) {
            return this.checkUserPolicies('policy', { skipValidation: true })
        }

        return false
    }

    async restoreSessionPolicy(history, step, { identityFromUrl, plateFromUrl, policyNumberFromUrl } = {}) {
        const session = getLightSession() || {}
        const identity = identityFromUrl || this.identityNumber?.value || session.identityNumber

        const urlHasPlate = Boolean(normalizePlate(plateFromUrl))
        const urlHasPolicyNumber = Boolean(policyNumberFromUrl && String(policyNumberFromUrl).trim())

        let plate
        let policyNumber

        if (urlHasPlate || urlHasPolicyNumber) {
            // Deep link / detalle de póliza: reemplazar identificadores de la soft session.
            // Si viene policyNumber sin plate, limpiar patente anterior (y viceversa).
            plate = urlHasPlate ? normalizePlate(plateFromUrl) : null
            policyNumber = urlHasPolicyNumber ? String(policyNumberFromUrl).trim() : null
        } else {
            plate = normalizePlate(this.plate?.value || session.plate)
            policyNumber = this.policyNumberInput?.value || session.policyNumber || null
        }

        if (identity && identity !== this.identityNumber?.value) {
            this.setIdentityNumber(identity)
        }

        if (urlHasPlate || urlHasPolicyNumber) {
            this.setPlate(plate)
            this.setPolicyNumberInput(policyNumber)
            this.policyStore.clearPolicyData()
        } else {
            if (plate && plate !== this.plate?.value) {
                this.setPlate(plate)
            }

            if (policyNumber && policyNumber !== this.policyNumberInput?.value) {
                this.setPolicyNumberInput(policyNumber)
            }
        }

        if (!this.identityNumber?.value && !this.policyNumberInput?.value) {
            this.goToCredentials(history, step)
            return false
        }

        if (this.policyId) {
            this.persistLightSession()
            return true
        }

        const ok = await this.ensurePolicyLoaded()

        if (!ok) {
            this.clearVehicleLightSession()
            this.goToCredentials(history, step)
            return false
        }

        this.persistLightSession()
        return true
    }

    setPlatforms(value) {
        this.platforms = value
        this.setLoadingPlatforms(false)
    }

    setLoadingPlatforms(value) {
        this.loadingPlatforms = value
    }

    setPlatformResolved(value) {
        this.platformResolved = value
    }

    setPlatform(value) {
        this.platform = value
    }

    setMetatitle(value) {
        this.metatitle = value
    }

    setLogo(value) {
        this.logo = value
    }

    setContactInfo(value) {
        this.contactInfo = value
    }

    setLinks(value) {
        this.links = value
    }

    setFooter(value) {
        this.footer = value
    }

    setColors(value) {
        this.colors = value
    }

    setDefaultPlatform() {
        const defaultPlatform = platformsMock.platforms[0]

        this.setPlatform(defaultPlatform.name)
        this.setMetatitle(defaultPlatform.metatitle)
        this.setLogo(defaultPlatform.logo)
        this.setContactInfo(defaultPlatform.contact)
        this.setLinks(defaultPlatform.links)
        this.setFooter(defaultPlatform.footer)
        this.setColors(defaultPlatform.colors)
    }

    setStep(value) {
        this.step = value
    }

    setIdentityNumber(value) {
        this.identityNumber.setValue(value)
    }

    setCaptcha(value) {
        this.captcha.setValue(value)
    }

    setPlate(value) {
        this.plate.setValue(value)
    }

    setPolicyNumberInput(value) {
        if (value === null || value === undefined || value === '') {
            this.policyNumberInput.setValue(value)
            return
        }

        this.policyNumberInput.setValue(normalizePolicyNumber(value))
    }

    setPolicyData(value) {
        this.policyStore.setPolicyData(value)
        this.paymentRejectionStore.hydrateFromPolicy(value)
    }

    setInspection(value) {
        this.inspectionStore.setInspection(value)
    }

    setInspectionFiles(value) {
        this.inspectionStore.setInspectionFiles(value)
    }

    setEmail(value) {
        this.inspectionStore.setEmail(value)
    }

    setCollections(value) {
        this.paymentRejectionStore.setCollections(value)
    }

    setPaymentRejections(value) {
        this.paymentRejectionStore.setPaymentRejections(value)
    }

    async listPlatforms() {
        this.preRequest(this.listPlatforms)
        this.setLoadingPlatforms(true)

        try {
            const dynamicPlatforms = await this.customerSelfManagementService.getPlatforms()

            const mergedPlatforms = [...STATIC_PLATFORMS, ...dynamicPlatforms.filter(
                (platform) => !STATIC_PLATFORMS.some((staticPlatform) => staticPlatform.name === platform.name)
            )]

            runInAction(() => {
                this.setPlatforms(mergedPlatforms)
                this.onSuccessRequest()
            })

            return true
        } catch (error) {
            runInAction(() => {
                this.setPlatforms(STATIC_PLATFORMS)
                this.onSuccessRequest()
            })

            return true
        }
    }

    async checkCaptcha() {
        this.preRequest(this.checkCaptcha)
        await this.captcha?.validate()

        if (this.captcha.error) {
            this.finishRequest()
            return false
        }

        try {
            if (!validateCaptcha(this.captcha?.value)) {
                throw new Error()
            }

            runInAction(() => {
                this.onSuccessRequest()
            })

            return true
        } catch (error) {
            this.setCaptcha('')
            this.onErrorRequest('El código ingresado no coincide')
            return false
        }
    }

    async checkIdentityNumber() {
        this.preRequest(this.checkIdentityNumber)
        await this.identityNumber?.validate()

        if (this.identityNumber.error) {
            this.finishRequest()
            return false
        }

        try {
            await this.customerSelfManagementService.checkCustomer(this.identityNumber?.value, this.metatitle)

            runInAction(() => {
                this.onSuccessRequest()
                this.persistIdentityLightSession()
            })

            return true
        } catch (err) {
            runInAction(() => {
                this.onErrorRequest(err?.message ? err?.message : 'Error inesperado, intente nuevamente')
                this.clearIdentityLightSession()
            })
            return false
        }
    }

    async checkUserPolicies(selectedOption = 'plate', { skipValidation = false, skipPersist = false } = {}) {
        this.preRequest(this.checkUserPolicies)

        const optionMap = {
            vehicle: 'plate',
            otherRisks: 'policy',
            plate: 'plate',
            policy: 'policy',
        }
        const option = optionMap[selectedOption] || 'plate'

        if (option === 'plate') {
            const plate = normalizePlate(this.plate?.value)

            if (plate && plate !== this.plate?.value) {
                this.setPlate(plate)
            }

            if (!skipValidation) {
                await this.plate?.validate()

                if (this.plate.error) {
                    this.finishRequest()
                    return false
                }
            } else if (!plate) {
                this.finishRequest()
                return false
            }

            try {
                const { data: policyData } = await this.customerSelfManagementService.getPolicy(this.plate?.value, {
                    identityNumber: this.identityNumber?.value,
                    platform: this.metatitle,
                })

                runInAction(() => {
                    this.setPolicyNumberInput(null)
                    this.setPolicyData(policyData)
                    this.syncSessionVehicleFromPolicy()
                    this.onSuccessRequest()

                    if (!skipPersist) {
                        this.persistLightSession()
                    }
                })

                return true
            } catch (error) {
                runInAction(() => {
                    this.onErrorRequest(error?.message ? error?.message : 'Error inesperado, intente nuevamente')
                    this.clearVehicleLightSession()
                })
                return false
            }
        }

        if (!skipValidation) {
            await this.policyNumberInput?.validate()

            if (this.policyNumberInput.error) {
                this.finishRequest()
                return false
            }
        } else if (!this.policyNumberInput?.value) {
            this.finishRequest()
            return false
        }

        try {
            const { data: policyData } = await this.customerSelfManagementService.getPolicyByNumber(
                this.policyNumberInput?.value,
                {
                    identityNumber: this.identityNumber?.value,
                    platform: this.metatitle,
                },
            )

            runInAction(() => {
                this.setPlate(null)
                this.setPolicyData(policyData)
                this.onSuccessRequest()

                if (!skipPersist) {
                    this.persistLightSession()
                }
            })

            return true
        } catch (error) {
            runInAction(() => {
                this.onErrorRequest(error?.message ? error?.message : 'Error inesperado, intente nuevamente')
                this.clearVehicleLightSession()
            })
            return false
        }
    }

    async getPolicyByNumber(policyNumber) {
        this.preRequest(this.getPolicyByNumber)

        try {
            const { data: policyData } = await this.customerSelfManagementService.getPolicyByNumber(policyNumber, {
                identityNumber: this.identityNumber?.value,
                platform: this.metatitle,
            })

            runInAction(() => {
                this.setPolicyData(policyData)
                this.onSuccessRequest()
            })

            return true
        } catch (error) {
            this.onErrorRequest(error?.message ? error?.message : 'Error inesperado, intente nuevamente')
            return false
        }
    }

    async getInspectionFromCustomer() {
        return this.inspectionStore.getInspectionFromCustomer()
    }

    async getInspectionFromPolicy(policyId) {
        return this.inspectionStore.getInspectionFromPolicy(policyId)
    }

    async getInspection(id) {
        return this.inspectionStore.getInspection(id)
    }

    async sendInspection() {
        return this.inspectionStore.sendInspection()
    }

    getInspectionFiles() {
        return this.inspectionStore.getInspectionFiles()
    }

    get shouldSendInspection() {
        return this.inspectionStore.shouldSendInspection
    }

    setPlatformByPath(path) {
        this.setPlatformResolved(false)

        const prefix = `${CUSTOMER_SELF_MANAGEMENT}/`
        const slug = path.startsWith(prefix) ? path.slice(prefix.length).split('/')[0] || '' : ''

        const platformData = slug ? this.platforms.find((p) => p?.name === slug) : null

        if (platformData) {
            this.setPlatform(platformData.name)
            this.setMetatitle(platformData.metatitle)
            this.setLogo(platformData.logo)
            this.setContactInfo(platformData.contact)
            this.setLinks(platformData.links)
            this.setFooter(platformData.footer)
            this.setColors(platformData.colors)
        } else {
            this.setDefaultPlatform()
        }

        this.setPlatformResolved(true)
    }

    resetInspection() {
        this.inspectionStore.resetInspection()
    }

    resetStore() {
        this.setIdentityNumber(null)
        this.setCaptcha(null)
        this.setPlate(null)
        this.setPolicyNumberInput(null)
        this.setPolicyData({
            policyId: null,
            policyNumber: null,
            insuredName: null,
            insuranceCompany: null,
            riskType: null,
            assetDescription: null,
            validFrom: null,
            validTo: null,
            documents: [],
            collections: { status: null, message: null },
            paymentRejections: [],
            paymentMethods: []
        })
        this.inspectionStore.resetInspection()
        this.setStep(null)
        this.clearErrors()
    }

    clearErrors() {
        this.identityNumber?.clearError()
        this.captcha?.clearError()
        this.plate?.clearError()
        this.policyNumberInput?.clearError()
        this.inspectionStore?.email?.clearError()
        this.clearError()
    }

    json() {
        return this.inspectionStore.json()
    }

    get policyId() {
        return this.policyStore.policyId
    }

    get policyNumber() {
        return this.policyStore.policyNumber
    }

    get sessionVehicleLabel() {
        if (this.sessionPlate) {
            return this.sessionPlate
        }

        return this.sessionPolicyNumber ? String(this.sessionPolicyNumber) : null
    }

    get sessionVehicleKind() {
        if (this.sessionPlate) {
            return 'plate'
        }

        if (this.sessionPolicyNumber) {
            return 'policy'
        }

        return null
    }

    get insuredName() {
        return this.policyStore.insuredName
    }

    get insuranceCompany() {
        return this.policyStore.insuranceCompany
    }

    get riskType() {
        return this.policyStore.riskType
    }

    get asset() {
        return this.policyStore.asset
    }

    get validFrom() {
        return this.policyStore.validFrom
    }

    get validTo() {
        return this.policyStore.validTo
    }

    get documents() {
        return this.policyStore.documents
    }

    get collections() {
        return this.paymentRejectionStore.collections
    }

    get paymentRejections() {
        return this.paymentRejectionStore.paymentRejections
    }

    get paymentMethods() {
        return this.paymentRejectionStore.paymentMethods
    }

    get inspection() {
        return this.inspectionStore.inspection
    }

    get inspectionFiles() {
        return this.inspectionStore.inspectionFiles
    }

    get email() {
        return this.inspectionStore.email
    }

    makeObservables() {
        makeObservable(this, {
            platforms: observable,
            loadingPlatforms: observable,
            platformResolved: observable,
            platform: observable,
            metatitle: observable,
            logo: observable,
            contactInfo: observable,
            links: observable,
            footer: observable,
            colors: observable,
            step: observable,
            sessionIdentityNumber: observable,
            sessionPlate: observable,
            sessionPolicyNumber: observable,
            identityNumber: observable,
            captcha: observable,
            plate: observable,
            policyNumberInput: observable,
            policyId: computed,
            policyNumber: computed,
            sessionVehicleLabel: computed,
            sessionVehicleKind: computed,
            insuredName: computed,
            insuranceCompany: computed,
            riskType: computed,
            asset: computed,
            validFrom: computed,
            validTo: computed,
            documents: computed,
            collections: computed,
            paymentRejections: computed,
            paymentMethods: computed,
            inspection: computed,
            inspectionFiles: computed,
            email: computed,
            listPlatforms: action,
            setPlatforms: action,
            setLoadingPlatforms: action,
            setPlatformResolved: action,
            setPlatform: action,
            setMetatitle: action,
            setLogo: action,
            setContactInfo: action,
            setLinks: action,
            setFooter: action,
            setColors: action,
            setDefaultPlatform: action,
            setStep: action,
            hydrateLightSession: action,
            persistLightSession: action,
            persistIdentityLightSession: action,
            clearVehicleLightSession: action,
            clearIdentityLightSession: action,
            closeSession: action,
            stepFromPath: action,
            changeVehicleSession: action,
            syncSessionVehicleFromPolicy: action,
            ensurePolicyLoaded: action,
            restoreSessionPolicy: action,
            goToCredentials: action,
            setIdentityNumber: action,
            setCaptcha: action,
            setPlate: action,
            setPolicyNumberInput: action,
            setPolicyData: action,
            setInspection: action,
            setInspectionFiles: action,
            setEmail: action,
            setCollections: action,
            setPaymentRejections: action,
            checkCaptcha: action,
            checkIdentityNumber: action,
            checkUserPolicies: action,
            getPolicyByNumber: action,
            getInspectionFromCustomer: action,
            getInspectionFromPolicy: action,
            getInspection: action,
            sendInspection: action,
            getInspectionFiles: action,
            setPlatformByPath: action,
            resetInspection: action,
            resetStore: action,
            clearErrors: action,
            shouldSendInspection: computed,
        })
    }
}

export default CustomerSelfManagementStore
